// The loading rule's time ladder (docs/design/states-pass.md, part 1), shared by the office screens
// that load their own lists and sheets: useLadder says which step a wait is on, and LoadLine fills
// the slot for its words, reserved from the first frame so nothing moves.

import { useEffect, useState } from 'react';

/** The loading ladder (docs/design/states-pass.md): nothing new for 300 ms, placeholders, a line at 3 s, the error at 10 s. */
export type Phase = 'wait' | 'ph' | 'slow' | 'error';
export function useLadder(waiting: boolean, attempt: number, failed: boolean): Phase | null {
  const [phase, setPhase] = useState<Phase | null>(null);
  useEffect(() => {
    if (!waiting) {
      setPhase(null);
      return;
    }
    setPhase('wait');
    const ts = [
      setTimeout(() => setPhase((p) => (p === 'wait' ? 'ph' : p)), 300),
      setTimeout(() => setPhase((p) => (p === 'error' ? p : 'slow')), 3000),
      setTimeout(() => setPhase('error'), 10_000),
    ];
    return () => ts.forEach(clearTimeout);
  }, [waiting, attempt]);
  return waiting && failed ? 'error' : phase;
}

/** The line that names the wait, or the error with Try again, in a slot that is there from the first frame. */
export function LoadLine({
  phase,
  what,
  note,
  onRetry,
}: {
  phase: Phase | null;
  what: string;
  note?: string;
  onRetry: () => void;
}) {
  return (
    <>
      {phase === 'error' ? (
        <p className="ldline late" id="loadError">
          Couldn’t get {what}.{' '}
          <button className="btn soft" onClick={onRetry}>
            Try again
          </button>
        </p>
      ) : (
        <p className="ldline" id="loadSay">
          {phase === 'slow' ? `Getting ${what}…` : (note ?? '')}
        </p>
      )}
      <p className="ld-live" aria-live="polite">
        {phase === 'slow' ? `Getting ${what}…` : phase === 'error' ? `Couldn’t get ${what}. Try again.` : ''}
      </p>
    </>
  );
}
